import React from "react";
import css from "@styled-system/css";
import { GatsbyImage, getImage } from "gatsby-plugin-image";

import { PrismicColor } from "utils";

import {
  AspectRatio,
  Box,
  Column,
  Columns,
  Heading,
  RichText,
  Text,
  VStack,
  Wrapper,
} from "components";

function Person({ headshot, name, role, content }) {
  return (
    <VStack space="layout.1">
      <VStack space={4}>
        {headshot && (headshot.gatsbyImageData || headshot.url) && (
          <Box width={1 / 2}>
            <AspectRatio ratio="3:4" position="relative">
              {headshot.gatsbyImageData ? (
                <GatsbyImage
                  image={getImage(headshot.gatsbyImageData)}
                  alt={headshot.alt || ""}
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                  }}
                  imgStyle={{ objectFit: "cover", objectPosition: "50% 50%" }}
                />
              ) : (
                // Fallback: some images have no processed gatsbyImageData
                // (e.g. an unsupported source format) — render the raw URL.
                <Box
                  as="img"
                  src={headshot.url}
                  alt={headshot.alt || ""}
                  position="absolute"
                  top={0}
                  left={0}
                  width="100%"
                  height="100%"
                  css={{ objectFit: "cover", objectPosition: "50% 50%" }}
                />
              )}
            </AspectRatio>
          </Box>
        )}
        {name && name.text && <Text children={name.text} as="h3" size={600} />}
        {role && <Text children={role} size={600} color="label.1" />}
      </VStack>
      {content && content.html && <RichText html={content.html} size={500} />}
    </VStack>
  );
}

export function PeopleList({ title, description, items, background }) {
  if (items && items[0] && items[0].name && items[0].name.text)
    return (
      <Wrapper
        id={title && title.text}
        py={{ _: "layout.5", md: "layout.6" }}
        bg={PrismicColor(background)}
      >
        <Columns spaceX="layout.5" spaceY="layout.4">
          {/* Title and description*/}
          <Column width={{ _: 1 / 1, lg: 4 / 12 }}>
            <VStack space="layout.4">
              {title && title.text && (
                <Heading children={title.text} as="h2" size={700} />
              )}
              {description && description.text && (
                <Text
                  children={description.text}
                  as="p"
                  size={500}
                  maxWidth="32ch"
                />
              )}
            </VStack>
          </Column>

          {/* Items */}
          <Column width={{ _: 1 / 1, xlg: 8 / 12 }}>
            <Box
              position="relative"
              css={css({
                "::before": {
                  content: "''",
                  position: "absolute",
                  top: "layout.3",
                  bottom: "layout.3",
                  left: -32,
                  display: ["none", null, null, "block"],
                  width: "1px",
                  bg: "separator",
                },
                "::after": {
                  content: "''",
                  position: "absolute",
                  top: "layout.3",
                  bottom: "layout.3",
                  left: "50%",
                  display: ["none", null, null, "block"],
                  width: "1px",
                  bg: "separator",
                },
              })}
            >
              <Columns
                as="ul"
                spaceX="layout.5"
                spaceY={{ _: "layout.2", lg: "layout.3" }}
              >
                {items.map((item, i) => (
                  <Column width={{ _: 1 / 1, lg: 4 / 8 }} key={`person-${i}`}>
                    {item && <Person {...item} />}
                  </Column>
                ))}
              </Columns>
            </Box>
          </Column>
        </Columns>
      </Wrapper>
    );
  else return null;
}
